import { Dialog, Transition } from '@headlessui/react';
import { CSSProperties, Fragment, useEffect, useRef } from 'react';
import { ChipProps } from '../../atoms/Chip';
import { ModalHeader } from '../../organism/ModalHeader';

type Position =
  | 'bottom'
  | 'bottom-left'
  | 'bottom-right'
  | 'top'
  | 'top-left'
  | 'top-right'
  | 'left'
  | 'right'
  | 'center';

type Size = 'sm' | 'md' | 'lg' | 'auto';
interface iModalProps {
  children?: React.ReactNode;
  position?: Position;
  size?: Size;
  className?: string;

  title?: string;
  subtitle?: string;
  chip?: ChipProps;

  isOpen: boolean;
  onClose: () => void;

  headerType?: 'LiveEventDetail' | 'default';
  noPadding?: boolean;
  style?: CSSProperties;
}

function Modal({
  children,
  className,
  title,
  subtitle,
  chip,
  position = 'center',
  size = 'md',
  isOpen,
  onClose,
  headerType = 'default',
  noPadding = false,
  style,
}: iModalProps) {
  const cardPositionClass = {
    bottom: 'justify-center items-end',
    'bottom-left': 'justify-start items-end',
    'bottom-right': 'justify-end items-end',

    top: 'justify-center items-start',
    'top-left': 'justify-start items-start',
    'top-right': 'justify-end items-start',

    left: 'justify-start items-center',
    right: 'justify-end items-center',
    center: 'justify-center items-center',
  };

  const maxSizeClass = {
    sm: 'max-w-screen-sm',
    md: 'max-w-screen-md',
    lg: 'max-w-screen-lg',
    auto: 'max-w-none !w-auto',
  };

  const showHeader = !!title || !!subtitle;
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const scrollToTop = () => scrollContainerRef.current?.scrollTo(0, 0);
    const rafId = requestAnimationFrame(scrollToTop);
    const timeoutId = window.setTimeout(scrollToTop, 350);
    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const resetPaddingRight = () => {
      if (document.body.style.paddingRight) {
        document.body.style.paddingRight = '';
      }
      if (document.documentElement.style.paddingRight) {
        document.documentElement.style.paddingRight = '';
      }
    };

    const observer = new MutationObserver(resetPaddingRight);

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style'],
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style'],
    });

    resetPaddingRight();

    return () => {
      observer.disconnect();
      resetPaddingRight();
    };
  }, [isOpen]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as='div' className={`relative z-10 flex`} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-mono-color-black/70' />
        </Transition.Child>

        <div
          ref={scrollContainerRef}
          data-modal-scroll
          className={`fixed inset-0 flex flex-wrap overflow-y-auto ${cardPositionClass[position]}`}
        >
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 scale-95'
            enterTo='opacity-100 scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 scale-100'
            leaveTo='opacity-0 scale-95'
          >
            <Dialog.Panel
              style={style}
              className={`
                w-full
                md:m-4  
                ${maxSizeClass[size]} 

                transform
                overflow-hidden 

                rounded-lg 

                bg-color-lightest 
                text-left 

                align-middle 
                shadow-level-4 

                transition-all 
                
                
                ${className}`}
            >
              {showHeader && (
                <ModalHeader
                  title={title}
                  subtitle={subtitle}
                  chip={chip}
                  onClose={onClose}
                  type={headerType}
                />
              )}

              <div
                className={`flex h-full flex-auto flex-col ${noPadding ? 'p-0' : 'p-4'}`}
              >
                {children}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

export { Modal };
